// @ts-nocheck
import { supabase } from '@/integrations/supabase/client';
import { notificationService } from './notificationService';

// Função para adicionar notificação interna (será injetada pelo contexto)
let addInternalNotification: ((notification: any) => void) | null = null;

export const setInternalNotificationHandler = (handler: (notification: any) => void) => {
  addInternalNotification = handler;
};

// Sistema de controle de notificações visualizadas
const VIEWED_NOTIFICATIONS_KEY = 'birthday-notifications-viewed';

interface ViewedNotification {
  clientId: string;
  date: string; // Data da notificação (YYYY-MM-DD)
  viewedAt: string; // Timestamp de quando foi visualizada
}

/**
 * Obtém notificações já visualizadas do localStorage
 */
const getViewedNotifications = (): ViewedNotification[] => {
  try {
    const stored = localStorage.getItem(VIEWED_NOTIFICATIONS_KEY);
    if (!stored) return [];
    const viewed: ViewedNotification[] = JSON.parse(stored);
    
    // Limpar notificações antigas (mais de 7 dias)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    return viewed.filter(n => new Date(n.viewedAt) > sevenDaysAgo);
  } catch (error) {
    console.error('❌ Erro ao obter notificações visualizadas:', error);
    return [];
  }
};

/**
 * Verifica se uma notificação já foi visualizada hoje
 */
const isNotificationViewed = (clientId: string, date: string): boolean => {
  const viewed = getViewedNotifications();
  return viewed.some(n => n.clientId === clientId && n.date === date);
};

/**
 * Marca uma notificação como visualizada
 */
export const markNotificationAsViewed = (clientId: string): void => {
  try {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const viewed = getViewedNotifications();
    
    // Verificar se já não está marcada
    if (!isNotificationViewed(clientId, today)) {
      viewed.push({
        clientId,
        date: today,
        viewedAt: new Date().toISOString()
      });
      
      localStorage.setItem(VIEWED_NOTIFICATIONS_KEY, JSON.stringify(viewed));
      console.log('✅ Notificação marcada como visualizada:', clientId);
    }
  } catch (error) {
    console.error('❌ Erro ao marcar notificação como visualizada:', error);
  }
};

export interface BirthdayClient {
  id: string;
  name: string;
  email: string;
  birth_date: string;
  phone?: string;
}

export interface BirthdayNotification {
  client: BirthdayClient;
  isToday: boolean;
  isTomorrow: boolean;
  age?: number;
}

class BirthdayNotificationService {
  private checkTimeout: NodeJS.Timeout | null = null;
  private lastCheckedDate: string = '';
  private isRunning: boolean = false;

  /**
   * Inicia o serviço de verificação de aniversários
   */
  public async startBirthdayMonitoring(): Promise<void> {
    // Evita múltiplas inicializações
    if (this.isRunning) {
      console.log('🎂 Serviço de aniversários já está rodando');
      return;
    }
    
    console.log('🎂 Iniciando monitoramento de aniversários...');
    this.isRunning = true;
    
    // Verifica imediatamente na primeira execução
    await this.checkBirthdays();
    
    // Agenda próxima verificação para 6h da manhã do próximo dia
    this.scheduleNextCheck();
  }

  /**
   * Para o serviço de monitoramento
   */
  public stopBirthdayMonitoring(): void {
    if (this.checkTimeout) {
      clearTimeout(this.checkTimeout);
      this.checkTimeout = null;
      this.isRunning = false;
      console.log('🎂 Monitoramento de aniversários parado');
    }
  }

  /**
   * Agenda próxima verificação para 6h da manhã do próximo dia
   */
  private scheduleNextCheck(): void {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    tomorrow.setHours(6, 0, 0, 0); // 6h da manhã
    
    const timeUntilNextCheck = tomorrow.getTime() - now.getTime();
    
    console.log(`🎂 Próxima verificação agendada para: ${tomorrow.toLocaleString('pt-BR')}`);
    
    this.checkTimeout = setTimeout(async () => {
      await this.checkBirthdays();
      // Agenda próxima verificação após executar
      this.scheduleNextCheck();
    }, timeUntilNextCheck);
  }

  /**
   * Verifica aniversários e envia notificações
   */
  private async checkBirthdays(): Promise<void> {
    try {
      const today = new Date();
      const todayString = today.toISOString().split('T')[0];
      
      // Evita verificar múltiplas vezes no mesmo dia
      if (this.lastCheckedDate === todayString) {
        console.log('🎂 Aniversários já verificados hoje, aguardando próximo dia');
        return;
      }
      
      this.lastCheckedDate = todayString;
      
      console.log(`🎂 Verificando aniversários para ${todayString} às ${today.toLocaleTimeString('pt-BR')}`);
      
      const birthdayNotifications = await this.getBirthdayNotifications();
      
      if (birthdayNotifications.length > 0) {
        console.log(`🎂 Encontrados ${birthdayNotifications.length} aniversários para notificar`);
        await this.sendBirthdayNotifications(birthdayNotifications);
      } else {
        console.log('🎂 Nenhum aniversário encontrado para hoje ou amanhã');
      }
      
    } catch (error) {
      console.error('❌ Erro ao verificar aniversários:', error);
    }
  }

  /**
   * Busca clientes com aniversários hoje ou amanhã
   */
  private async getBirthdayNotifications(): Promise<BirthdayNotification[]> {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    const todayMonthDay = `${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const tomorrowMonthDay = `${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
    
    console.log('🎂 Verificando aniversários para:', { todayMonthDay, tomorrowMonthDay });
    
    // Busca todos os clientes com birth_date para filtrar localmente
    const { data: allClients, error } = await supabase
      .from('clients')
      .select('id, name, email, birth_date, phone')
      .not('birth_date', 'is', null);
    
    if (error) {
      console.error('❌ Erro ao buscar clientes:', error);
      return [];
    }
    
    if (!allClients || allClients.length === 0) {
      console.log('🎂 Nenhum cliente com data de nascimento encontrado');
      return [];
    }
    
    // Filtrar clientes com aniversários hoje ou amanhã
    const clients = allClients.filter(client => {
      if (!client.birth_date) return false;
      
      const birthDate = new Date(client.birth_date);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      
      const isToday = birthDate.getMonth() === today.getMonth() && 
                     birthDate.getDate() === today.getDate();
      
      const isTomorrow = birthDate.getMonth() === tomorrow.getMonth() && 
                        birthDate.getDate() === tomorrow.getDate();
      
      return isToday || isTomorrow;
    });
    
    if (clients.length === 0) {
      console.log('🎂 Nenhum aniversário encontrado para hoje ou amanhã');
      return [];
    }
    
    console.log('🎂 Clientes com aniversário encontrados:', clients.length);
    
    // Processa as notificações
    const notifications: BirthdayNotification[] = clients.map(client => {
      const birthDate = new Date(client.birth_date);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      
      const isToday = birthDate.getMonth() === today.getMonth() && 
                     birthDate.getDate() === today.getDate();
      
      const isTomorrow = birthDate.getMonth() === tomorrow.getMonth() && 
                        birthDate.getDate() === tomorrow.getDate();
      
      const age = today.getFullYear() - birthDate.getFullYear();
      
      return {
        client,
        isToday,
        isTomorrow,
        age
      };
    });
    
    return notifications;
  }

  /**
   * Envia notificações de aniversário
   */
  private async sendBirthdayNotifications(notifications: BirthdayNotification[]): Promise<void> {
    for (const notification of notifications) {
      try {
        if (notification.isToday) {
          await this.sendTodayNotification(notification);
        } else if (notification.isTomorrow) {
          await this.sendTomorrowNotification(notification);
        }
      } catch (error) {
        console.error('❌ Erro ao enviar notificação de aniversário para', notification.client.name, ':', error);
        // Continua com as próximas notificações mesmo se uma falhar
      }
    }
  }

  /**
   * Envia notificação de aniversário hoje
   */
  private async sendTodayNotification(notification: BirthdayNotification): Promise<void> {
    const { client, age } = notification;
    const today = new Date().toISOString().split('T')[0];
    
    // Verificar se já foi visualizada hoje
    if (isNotificationViewed(client.id, today)) {
      console.log('⏭️ Notificação já visualizada hoje para', client.name);
      return;
    }
    
    const title = '🎂 Aniversário Hoje!';
    const message = `${client.name} está fazendo ${age} anos hoje! Não se esqueça de parabenizá-lo.`;
    
    console.log('🎂 Notificação de aniversário hoje:', { client: client.name, age });
    
    // Adiciona notificação ao sistema existente (navegador)
    await notificationService.showNotification({
      id: `birthday-today-${client.id}-${Date.now()}`,
      type: 'reminder',
      title,
      message,
      priority: 'high',
      isRead: false,
      createdAt: new Date().toISOString(),
      relatedId: client.id,
      relatedType: 'client',
      actionUrl: `/clients?filter=birthday&clientId=${client.id}`,
      icon: '🎂'
    });

    // Adiciona notificação interna (sininho)
    if (addInternalNotification) {
      addInternalNotification({
        type: 'reminder',
        title,
        message,
        priority: 'high',
        relatedId: client.id,
        relatedType: 'client',
        actionUrl: `/clients?filter=birthday&clientId=${client.id}`,
        icon: '🎂',
        source: 'birthday'
      });
    }
  }

  /**
   * Envia notificação de aniversário amanhã
   */
  private async sendTomorrowNotification(notification: BirthdayNotification): Promise<void> {
    const { client, age } = notification;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowDate = tomorrow.toISOString().split('T')[0];
    
    // Verificar se já foi visualizada
    if (isNotificationViewed(client.id, tomorrowDate)) {
      console.log('⏭️ Notificação de amanhã já visualizada para', client.name);
      return;
    }
    
    const title = '🎂 Aniversário Amanhã!';
    const message = `${client.name} fará ${age} anos amanhã. Prepare-se para parabenizá-lo!`;
    
    console.log('🎂 Notificação de aniversário amanhã:', { client: client.name, age });
    
    // Adiciona notificação ao sistema existente (navegador)
    await notificationService.showNotification({
      id: `birthday-tomorrow-${client.id}-${Date.now()}`,
      type: 'reminder',
      title,
      message,
      priority: 'normal',
      isRead: false,
      createdAt: new Date().toISOString(),
      relatedId: client.id,
      relatedType: 'client',
      actionUrl: `/clients?filter=birthday&clientId=${client.id}`,
      icon: '🎂'
    });

    // Adiciona notificação interna (sininho)
    if (addInternalNotification) {
      addInternalNotification({
        type: 'reminder',
        title,
        message,
        priority: 'normal',
        relatedId: client.id,
        relatedType: 'client',
        actionUrl: `/clients?filter=birthday&clientId=${client.id}`,
        icon: '🎂',
        source: 'birthday'
      });
    }
  }


  /**
   * Verifica aniversários manualmente (para testes)
   */
  public async checkBirthdaysManually(): Promise<BirthdayNotification[]> {
    console.log('🎂 Verificação manual de aniversários...');
    return await this.getBirthdayNotifications();
  }

  /**
   * Obtém estatísticas de aniversários
   */
  public async getBirthdayStats(): Promise<{
    today: number;
    tomorrow: number;
    thisWeek: number;
    thisMonth: number;
  }> {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay());
    
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    
    const todayMonthDay = `${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const tomorrowMonthDay = `${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
    
    // Busca estatísticas
    const { data: todayClients } = await supabase
      .from('clients')
      .select('id')
      .not('birth_date', 'is', null)
      .like('birth_date', `%-${todayMonthDay}`);
    
    const { data: tomorrowClients } = await supabase
      .from('clients')
      .select('id')
      .not('birth_date', 'is', null)
      .like('birth_date', `%-${tomorrowMonthDay}`);
    
    return {
      today: todayClients?.length || 0,
      tomorrow: tomorrowClients?.length || 0,
      thisWeek: 0, // Implementar se necessário
      thisMonth: 0  // Implementar se necessário
    };
  }

  /**
   * Testa o sistema de notificações de aniversário
   */
  public async testBirthdayNotification(): Promise<void> {
    console.log('🧪 Testando notificação de aniversário...');
    
    try {
      // Criar uma notificação de teste
      const testNotification: BirthdayNotification = {
        client: {
          id: 'test-client-id',
          name: 'Cliente Teste',
          email: 'teste@exemplo.com'
        },
        age: 30,
        birthDate: new Date(),
        type: 'today'
      };

      // Testar notificação de hoje
      await this.sendTodayNotification(testNotification);
      console.log('✅ Teste de notificação de hoje executado com sucesso');
      
      // Se não há handler interno configurado, adicionar notificação de teste diretamente
      if (!addInternalNotification) {
        console.log('⚠️ Handler de notificação interna não configurado. Configure o NotificationProvider.');
      }
      
    } catch (error) {
      console.error('❌ Erro no teste de notificação:', error);
    }
  }
}

export const birthdayNotificationService = new BirthdayNotificationService();
